import React, { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { FUNCTIONS_URL, SUPABASE_ANON_KEY } from '../lib/supabase';

// Public prospect-onboarding page, served at app.tutortracer.com/onboard/<token>
// (and onboard.tutortracer.com/<token> once that DNS exists). Deliberately standalone: no auth,
// no ThemeContext — a clean light page prospects see before they have an
// account. The token gates everything; the key goes straight to the
// onboard-intake edge function over HTTPS and is never kept client-side.
const ANON_KEY = SUPABASE_ANON_KEY;

const call = async (payload) => {
  const r = await fetch(`${FUNCTIONS_URL}/onboard-intake`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
    body: JSON.stringify(payload),
  });
  return r.json();
};

const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-[#d9d9de] bg-white text-[15px] text-[#1a1a1a] placeholder-[#a1a1aa] focus:outline-none focus:ring-2 focus:ring-[#586f8d]/40 focus:border-[#586f8d]';
const labelClass = 'block text-[13px] font-medium text-[#444] mb-1.5';

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#f6f6f8] text-[#1a1a1a] flex flex-col">
    <div className="w-full max-w-xl mx-auto px-6 pt-14 pb-16 flex-1">
      <img src="/tutortracer_logowithtext_black_trpt.png" alt="TutorTracer" className="h-8 w-auto mb-10" />
      {children}
    </div>
    <div className="py-6 text-center text-xs text-[#999]">
      &copy; {new Date().getFullYear()} Sheppard Corporation Pty Ltd &middot;{' '}
      <a href="https://app.tutortracer.com/terms" className="underline">Terms</a> &middot;{' '}
      <a href="mailto:support@tutortracer.com" className="underline">support@tutortracer.com</a>
    </div>
  </div>
);

const DeadEnd = ({ title, body }) => (
  <Shell>
    <div className="bg-white border border-[#e5e5e5] rounded-2xl p-8 text-center">
      <AlertCircle className="h-8 w-8 mx-auto mb-3 text-[#b45309]" />
      <h1 className="text-lg font-semibold font-[Manrope] mb-1.5">{title}</h1>
      <p className="text-sm text-[#666] leading-relaxed">{body}</p>
    </div>
  </Shell>
);

const OnboardIntake = () => {
  // Token = last non-empty path segment ("/onboard/<token>" or "/<token>").
  const token = window.location.pathname.split('/').filter(Boolean).pop() || '';
  const looksLikeToken = /^[a-f0-9]{40,64}$/i.test(token);

  const [phase, setPhase] = useState('loading'); // loading | invalid | expired | used | form | submitting | done
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!looksLikeToken) { setPhase('invalid'); return; }
    (async () => {
      try {
        const res = await call({ action: 'validate', token });
        if (!res.ok) { setPhase('invalid'); return; }
        setCompanyName(res.company_name || '');
        if (res.contact_email) setEmail(res.contact_email);
        if (res.status === 'pending') setPhase('form');
        else if (res.status === 'expired') setPhase('expired');
        else setPhase('used');
      } catch {
        setPhase('invalid');
      }
    })();
  }, [token, looksLikeToken]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid work email.'); return; }
    if (!apiKey.trim()) { setError('Please paste your Lessonspace API key.'); return; }
    setPhase('submitting');
    try {
      const res = await call({
        action: 'submit',
        token,
        api_key: apiKey.trim(),
        submitter_email: email.trim(),
        notes: notes.trim() || null,
      });
      if (res.ok) { setApiKey(''); setPhase('done'); return; }
      if (res.error === 'link_expired') { setPhase('expired'); return; }
      if (res.error === 'already_used') { setPhase('used'); return; }
      setError(res.error || 'Something went wrong — please try again.');
      setPhase('form');
    } catch {
      setError('Network error — please try again.');
      setPhase('form');
    }
  };

  if (phase === 'loading') {
    return (
      <Shell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[#586f8d]" />
        </div>
      </Shell>
    );
  }
  if (phase === 'invalid') {
    return <DeadEnd title="This link isn't valid" body="Check the link you were sent, or contact the TutorTracer team for a new one." />;
  }
  if (phase === 'expired') {
    return <DeadEnd title="This link has expired" body="For security, onboarding links only last a couple of weeks. Contact the TutorTracer team and we'll send a fresh one." />;
  }
  if (phase === 'used') {
    return (
      <Shell>
        <div className="bg-white border border-[#e5e5e5] rounded-2xl p-8 text-center">
          <CheckCircle className="h-8 w-8 mx-auto mb-3 text-emerald-600" />
          <h1 className="text-lg font-semibold font-[Manrope] mb-1.5">Already received</h1>
          <p className="text-sm text-[#666] leading-relaxed">
            We've already received the details for this link — each link can only be used once.
            If you need to correct something, contact the TutorTracer team.
          </p>
        </div>
      </Shell>
    );
  }
  if (phase === 'done') {
    return (
      <Shell>
        <div className="bg-white border border-[#e5e5e5] rounded-2xl p-8 text-center">
          <CheckCircle className="h-8 w-8 mx-auto mb-3 text-emerald-600" />
          <h1 className="text-lg font-semibold font-[Manrope] mb-1.5">All done — thank you</h1>
          <p className="text-sm text-[#666] leading-relaxed">
            Your details were received securely. The TutorTracer team will set up your account
            and be in touch shortly. You can close this page.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-8">
        <h1 className="text-2xl font-semibold font-[Manrope] tracking-tight">
          Welcome{companyName ? `, ${companyName}` : ''}
        </h1>
        <p className="mt-2 text-[15px] text-[#666] leading-relaxed">
          To connect your lessons to TutorTracer, we just need a Lessonspace API key. Paste it
          below and we'll finish setting up your account.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#e5e5e5] rounded-2xl p-7 space-y-5">
        <div>
          <label className={labelClass}>Your work email</label>
          <input type="email" required className={inputClass} value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />
        </div>
        <div>
          <label className={labelClass}>Lessonspace API key</label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              name="ls-api-key"
              autoComplete="new-password"
              className={`${inputClass} pr-11`}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="Paste your API key"
            />
            <button
              type="button"
              onClick={() => setShowKey(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#444]"
              aria-label={showKey ? 'Hide key' : 'Show key'}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-[#999]">
            Sign in to Lessonspace and open{' '}
            <a href="https://www.thelessonspace.com/settings/developer" target="_blank" rel="noopener noreferrer" className="underline hover:text-[#444]">Settings &rarr; Developer</a>
            {' '}to copy your organisation's API key.
          </p>
        </div>
        <div>
          <label className={labelClass}>Anything else we should know? <span className="text-[#999] font-normal">(optional)</span></label>
          <textarea rows={3} className={inputClass} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes for the TutorTracer team" />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={phase === 'submitting'}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-60"
        >
          {phase === 'submitting' ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : 'Submit securely'}
        </button>

        <div className="flex items-start gap-2.5 pt-1">
          <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#586f8d]" />
          <p className="text-xs text-[#999] leading-relaxed">
            Sent over an encrypted connection and stored encrypted. Your key is only used to sync
            your lessons into TutorTracer, and this link stops working once submitted.
          </p>
        </div>
      </form>
    </Shell>
  );
};

export default OnboardIntake;
